import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/api'
import { uiLogger } from '@/lib/debug'
import { applyAcceptedServerCancellation } from '@/lib/queries/serverQueries'
import { cancellationForServer, getServerCancellations, getServerCancellationStatus, requireServerCancellationReceipt, type ServerCancellationReceipt } from '@/lib/serverCancellation'
import { ServerCancellationContext, type ServerCancellationRequest } from '@/contexts/ServerCancellation'
import ServerCancellationProgressModal from './ServerCancellationProgressModal'

type Progress = {
  request: ServerCancellationRequest
  state: 'submitting' | 'accepted' | 'unconfirmed'
  receipt: ServerCancellationReceipt | null
}

/** Lives outside the server route so an accepted cancellation cannot unmount its receipt. */
export default function ServerCancellationProvider({ children }: { children: ReactNode }) {
  const [progress, setProgress] = useState<Progress | null>(null)
  const [statusUnavailable, setStatusUnavailable] = useState(false)
  const active = useRef<Progress | null>(null)
  const attempt = useRef(0)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  useEffect(() => () => { attempt.current += 1 }, [])

  const submit = useCallback(async (request: ServerCancellationRequest, recovering = false) => {
    const currentAttempt = ++attempt.current
    const pending: Progress = { request, state: 'submitting', receipt: null }
    active.current = pending
    setProgress(pending)
    setStatusUnavailable(false)
    let receipt: ServerCancellationReceipt | undefined
    const recover = async () => {
      try {
        return cancellationForServer(await getServerCancellations(), request.serverUuid)
      } catch {
        return undefined
      }
    }
    if (recovering) receipt = await recover()
    // An account switch/unmount must not dispatch a delayed retry under another session.
    if (attempt.current !== currentAttempt) return
    if (!receipt) {
      try {
        receipt = requireServerCancellationReceipt(
          await apiClient.cancelServerSubscription(request.serverUuid, request.disposition), request.serverUuid,
        )
      } catch {
        // The response can be lost after billing already stopped. Read the durable
        // receipt before showing uncertainty; a failed lookup is not failed deletion.
        if (attempt.current !== currentAttempt) return
        receipt = await recover()
      }
    }
    if (attempt.current !== currentAttempt) return
    const result: Progress = { request, state: receipt ? 'accepted' : 'unconfirmed', receipt: receipt || null }
    active.current = result
    setProgress(result)
    if (!receipt) return
    // UI/cache work must never downgrade a verified billing cutoff to failure.
    try {
      applyAcceptedServerCancellation(queryClient, receipt)
    } catch (error) {
      uiLogger.warn('Cancellation accepted; server cache refresh deferred:', error)
    }
    navigate('/servers')
  }, [navigate, queryClient])

  const begin = useCallback((request: ServerCancellationRequest) => {
    if (active.current) return
    void submit(request)
  }, [submit])

  const receipt = progress?.receipt
  useEffect(() => {
    if (!receipt || receipt.cleanup_status === 'completed') return
    let disposed = false
    let timer: ReturnType<typeof setTimeout>
    const poll = async () => {
      try {
        const latest = await getServerCancellationStatus(receipt)
        if (disposed) return
        setStatusUnavailable(false)
        setProgress(previous => previous?.receipt?.operation_id === receipt.operation_id
          ? { ...previous, receipt: latest } : previous)
        if (latest.cleanup_status === 'completed') return
      } catch {
        if (disposed) return
        // Confirmation remains valid even if cleanup or its status service is down.
        setStatusUnavailable(true)
      }
      if (!disposed) timer = setTimeout(() => void poll(), 5000)
    }
    timer = setTimeout(() => void poll(), 2500)
    return () => { disposed = true; clearTimeout(timer) }
    // Only restart for a different receipt; intermediate phases do not overlap polls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt?.operation_id])

  const close = () => {
    if (active.current?.state === 'submitting') return
    attempt.current += 1
    active.current = null
    setProgress(null)
  }

  return <ServerCancellationContext.Provider value={begin}>
    {children}
    {progress && <ServerCancellationProgressModal serverName={progress.request.serverName}
      state={progress.state} receipt={progress.receipt} statusUnavailable={statusUnavailable}
      onClose={close} onRetry={() => {
        if (active.current?.state === 'unconfirmed') void submit(active.current.request, true)
      }} />}
  </ServerCancellationContext.Provider>
}
